/**
 * MonthFlagsAlert — P35 (Pedro 2026-10-14): the month-over-month flags on the per-account
 * upload previews (20th fee basis; EOM AuM & flows), from the import report:
 *   New (first value ever → the account's start month), Missing (a value last month, none
 *   now), Zero (0 this month or last), Gap (last value older than last month).
 * Read-only, non-blocking; the runs name the same accounts if they stay unresolved.
 */
import { Alert, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

import type { AccountMetricsImportReport } from '../api/accountMetrics';

export function MonthFlagsAlert({ preview, kind }: {
  preview: AccountMetricsImportReport;
  /** What "value" means in this upload, for the wording. */
  kind: 'fee' | 'eom';
}) {
  const rows: { title: string; items: string[]; note: string }[] = [
    {
      title: 'New', items: preview.flag_new_accounts ?? [],
      note: kind === 'eom'
        ? 'first month-end value ever: this is the account\'s first month — its funding must be an inflow in this file'
        : 'first 20th value ever: this is the account\'s first month — its first fee is pro-rata',
    },
    {
      title: 'Missing', items: preview.flag_missing_accounts ?? [],
      note: 'had a value last month but none in this file or on record — forgotten?',
    },
    { title: 'Zero', items: preview.flag_zero_accounts ?? [], note: 'emptied, or a mistaken 0?' },
    {
      title: 'Gap', items: preview.flag_gap_accounts ?? [],
      note: 'no value last month — upload last month too, or the runs will stop on it',
    },
  ].filter((r) => r.items.length > 0);
  if (rows.length === 0) return null;
  return (
    <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}
      title="Accounts that changed since last month — please check">
      <Stack gap={6}>
        {rows.map((r) => (
          <Text key={r.title} size="sm">
            <b>{r.title} ({r.items.length})</b> — {r.note}: {r.items.join('  ·  ')}
          </Text>
        ))}
      </Stack>
    </Alert>
  );
}
